import { TrendingUp, TrendingDown, Sparkles, AlertTriangle } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

interface PredictiveCardProps {
  avgMonthlyRevenue: number
  growthTrend: number
  projectedNextMonth: number
  confidence: 'low' | 'medium' | 'high'
  monthsAnalyzed: number
}

const formatCurrency = (value: number) =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 })

const confidenceLabels = {
  low: { label: 'Baixa', color: 'bg-warning/10 text-warning border-warning/20' },
  medium: { label: 'Média', color: 'bg-primary/10 text-primary border-primary/20' },
  high: { label: 'Alta', color: 'bg-success/10 text-success border-success/20' }
}

export function PredictiveAnalysisCard({
  avgMonthlyRevenue,
  growthTrend,
  projectedNextMonth,
  confidence,
  monthsAnalyzed
}: PredictiveCardProps) {
  const isPositiveTrend = growthTrend >= 0
  const confidenceConfig = confidenceLabels[confidence]

  return (
    <Card className="shadow-card border-primary/20 bg-gradient-to-br from-primary/5 via-transparent to-transparent">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            Análise Preditiva
          </CardTitle>
          <Badge variant="outline" className={cn("text-xs", confidenceConfig.color)}>
            Confiança {confidenceConfig.label}
          </Badge>
        </div>
        <CardDescription>
          Baseado nos últimos {monthsAnalyzed} meses de dados reais
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Main projection */}
        <div className="p-4 bg-card rounded-xl border shadow-sm text-center">
          <p className="text-sm text-muted-foreground mb-1">Projeção próximo mês</p>
          <p className="text-3xl font-display font-bold text-primary">
            {formatCurrency(projectedNextMonth)}
          </p>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 bg-muted/50 rounded-xl">
            <p className="text-xs text-muted-foreground mb-1">Média mensal</p>
            <p className="text-lg font-semibold">{formatCurrency(avgMonthlyRevenue)}</p>
          </div>
          <div className="p-3 bg-muted/50 rounded-xl">
            <p className="text-xs text-muted-foreground mb-1">Tendência</p>
            <div className={cn(
              "flex items-center gap-1.5 text-lg font-semibold",
              isPositiveTrend ? "text-success" : "text-destructive"
            )}>
              {isPositiveTrend ? (
                <TrendingUp className="h-4 w-4" />
              ) : (
                <TrendingDown className="h-4 w-4" />
              )}
              {isPositiveTrend ? '+' : ''}{growthTrend.toFixed(1)}%
            </div>
          </div>
        </div>

        {/* Insight message */}
        <div className={cn(
          "p-3 rounded-xl border text-sm",
          isPositiveTrend 
            ? "bg-success/5 border-success/20 text-success" 
            : "bg-warning/5 border-warning/20 text-warning"
        )}>
          <div className="flex items-start gap-2">
            {isPositiveTrend ? (
              <TrendingUp className="h-4 w-4 mt-0.5 shrink-0" />
            ) : (
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            )}
            <p>
              {isPositiveTrend 
                ? `Com base na média dos últimos ${monthsAnalyzed} meses, a projeção para o próximo mês é de ${formatCurrency(projectedNextMonth)}.`
                : `Atenção: tendência de queda de ${Math.abs(growthTrend).toFixed(1)}% mensal. Considere ações para reverter.`
              }
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
